/**
 * Checkbox - 체크박스 컴포넌트
 *
 * @param indeterminate - 부분 선택 상태 (전체 선택 등에서 사용)
 * @param label / description - 체크박스 옆에 표시할 텍스트
 */
'use client';

import React, { useRef, useEffect, useId } from 'react';
import styles from './Checkbox.module.css';

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  indeterminate?: boolean;
  disabled?: boolean;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function Checkbox({
  checked,
  onChange,
  label,
  description,
  indeterminate = false,
  disabled = false,
  error,
  size = 'md',
  className = '',
}: CheckboxProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const errorId = useId();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <label className={`${styles.checkbox} ${disabled ? styles.disabled : ''} ${styles[size]}`}>
        <span className={styles.control}>
          <input
            ref={inputRef}
            type="checkbox"
            className={styles.input}
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
          />
          <span
            className={`${styles.box} ${checked ? styles.checked : ''} ${indeterminate ? styles.indeterminate : ''}`}
          >
            {checked && !indeterminate && (
              <svg viewBox="0 0 12 10" fill="none" className={styles.icon}>
                <path
                  d="M1 5.5L4 8.5L11 1.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {indeterminate && (
              <svg viewBox="0 0 10 2" fill="none" className={styles.icon}>
                <path d="M1 1H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </span>
        </span>
        {(label || description) && (
          <span className={styles.labelGroup}>
            {label && <span className={styles.label}>{label}</span>}
            {description && <span className={styles.description}>{description}</span>}
          </span>
        )}
      </label>
      {error && (
        <p className={styles.errorText} id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
